import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useSeedListCancelPlanContext } from "./seed-list-cancel-provider";
import { format } from "date-fns";
import { PaymentInterval } from "@/types/subscription.types";
import { Mail, Calendar, DollarSign, Users, Settings } from "lucide-react";
import { useMemo } from "react";

const SeedListCancelReviewPlan = () => {
  const { state, planDetails, subscription, searchParams, handleStateChange } =
    useSeedListCancelPlanContext();

  if (!planDetails || !subscription) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            {!planDetails && !subscription
              ? "Loading plan details..."
              : !planDetails
              ? "Plan details not found"
              : "Subscription not found"}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate seed list plan price
  const calculateSeedListPrice = useMemo(() => {
    // Seed list pricing: $0.30 per seed account, sender profiles cost 60% of base price each
    const SEED_PRICE_PER_UNIT = 0.30;
    const SENDER_PROFILE_MULTIPLIER = 0.60;

    // Get seed accounts and sender profiles from plan details
    // seedCountPerSender is the number of seeds per sender profile
    // quantity is the number of sender profiles
    const seedCountPerSender = planDetails.seedCountPerSender || 500;
    const senderProfiles = planDetails.quantity || planDetails.maxSenderProfilesMonthly || 1;
    
    // Total seed accounts = seedCountPerSender * quantity (number of sender profiles)
    const seedAccounts = seedCountPerSender * senderProfiles;

    // Calculate base price: seed accounts * $0.30
    const basePrice = seedAccounts * SEED_PRICE_PER_UNIT;

    // Calculate sender profile cost: 60% of base price per sender profile
    const senderProfileCost = basePrice * SENDER_PROFILE_MULTIPLIER * senderProfiles;

    // Final monthly price = base price + sender profile costs
    const finalPrice = basePrice + senderProfileCost;

    // Calculate price based on interval
    let totalPrice: number;
    if (subscription.interval === PaymentInterval.ANNUAL) {
      totalPrice = finalPrice * 12 * 0.8; // 20% discount
    } else if (subscription.interval === PaymentInterval.SEMI_YEARLY) {
      totalPrice = finalPrice * 6 * 0.85; // 15% discount
    } else if (subscription.interval === PaymentInterval.QUARTERLY) {
      totalPrice = finalPrice * 3 * 0.9; // 10% discount
    } else {
      totalPrice = finalPrice; // Monthly
    }

    return {
      monthlyPrice: finalPrice,
      totalPrice: totalPrice * searchParams.quantity,
      seedAccounts,
      senderProfiles,
      seedCountPerSender,
    };
  }, [planDetails, subscription, searchParams.quantity]);

  const renderPriceBasedOnInterval = () => {
    const { totalPrice } = calculateSeedListPrice;

    if (subscription.interval === PaymentInterval.ANNUAL) {
      return (totalPrice / 12).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.QUARTERLY) {
      return (totalPrice / 3).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.SEMI_YEARLY) {
      return (totalPrice / 6).toFixed(2);
    }

    return totalPrice.toFixed(2);
  };

  const handleContinue = () => {
    handleStateChange({ step: 2 });
  };

  // Calculate emails per day and per month
  const emailsPerDay = calculateSeedListPrice.seedAccounts * calculateSeedListPrice.senderProfiles;
  const emailsPerMonth = emailsPerDay * 30;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Your Seed List Plan</CardTitle>
        <CardDescription>
          Please review your current seed list subscription details before proceeding
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Plan Details</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Plan Type:</span>{" "}
                    {planDetails.isCustomPlan ? "Custom Plan" : "Seed List Plan"}
                  </p>
                  <p>
                    <span className="font-medium">ESP Types:</span>{" "}
                    {planDetails.espTypes?.join(", ") || "N/A"}
                  </p>
                  <p>
                    <span className="font-medium">Quantity:</span>{" "}
                    {searchParams.quantity}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 p-2 rounded-full">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Seed List Configuration</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Seeds per Sender:</span>{" "}
                    {calculateSeedListPrice.seedCountPerSender.toLocaleString()}
                  </p>
                  <p>
                    <span className="font-medium">Sender Profiles:</span>{" "}
                    {calculateSeedListPrice.senderProfiles}
                  </p>
                  {/* <p>
                    <span className="font-medium">Max Sender Profiles Monthly:</span>{" "}
                    {planDetails.maxSenderProfilesMonthly}
                  </p> */}
                  
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-4">
              <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Billing Information</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Billing Interval:</span>{" "}
                    {subscription.interval}
                  </p>
                  <p>
                    <span className="font-medium">Current Price:</span> $
                    {renderPriceBasedOnInterval()}/
                    {subscription.interval.toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-4">
              <div className="bg-amber-100 dark:bg-amber-900/20 p-2 rounded-full">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Subscription Status</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Status:</span>{" "}
                    {subscription.status}
                  </p>
                  <p>
                    <span className="font-medium">Active Until:</span>{" "}
                    {format(planDetails?.subscriptionEndDate || new Date(), "MMM d, yyyy")}
                  </p>
                  <p className="text-amber-600 dark:text-amber-400 mt-2">
                    Your subscription will remain active until the end of your
                    current billing cycle. After cancellation, you'll lose
                    access to all seed list features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={handleContinue}>Continue</Button>
      </CardFooter>
    </Card>
  );
};

export default SeedListCancelReviewPlan;

